/**
 * RichTextEditor — reusable rich text editor with code snippet support.
 * Uses react-quill with a clean minimal toolbar.
 */
import React, { useMemo } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './RichTextEditor.css';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: number;
  withCode?: boolean; // include code block options
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Enter text...',
  minHeight = 100,
  withCode = false,
}) => {
  const modules = useMemo(() => ({
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ...(withCode ? [['code-block', 'code']] : []),
      ['clean'],
    ],
  }), [withCode]);

  const formats = [
    'bold', 'italic', 'underline',
    'list', 'bullet',
    ...(withCode ? ['code-block', 'code'] : []),
  ];

  return (
    <div className="rte-wrapper" style={{ '--rte-min-height': `${minHeight}px` } as React.CSSProperties}>
      <ReactQuill
        theme="snow"
        value={value}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
      />
    </div>
  );
};

/** Strip HTML tags for plain text display in table cells */
export function stripHtml(html: string): string {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
}

/** Render HTML safely in read-only display */
export const RichTextDisplay: React.FC<{ html: string; className?: string }> = ({ html, className }) => (
  <div
    className={`rte-display ${className || ''}`}
    dangerouslySetInnerHTML={{ __html: html || '' }}
  />
);
